<script setup lang="ts">
import type { Color, ComponentSize, UIProp } from '~/types/elementsUi'
import type { FAIconName } from '#shared/types/generated-icons'
import VHtmlButton from '~/components/atoms/button/VHtmlButton.vue'
import type { ExtendedTailwindColor, TailwindColor } from '#shared/types/elementsUi'

interface VTagProps {
  label?: string
  color?: ExtendedTailwindColor
  textColor?: TailwindColor
  darkColor?: ExtendedTailwindColor
  darkTextColor?: ExtendedTailwindColor
  size?: ComponentSize
  rounded?: boolean
  isDelete?: boolean
  iconLeft?: FAIconName
  iconRight?: FAIconName
  iconColor?: TailwindColor
  stringColor?: Color
  cssTextColor?: Color
  overrideClickable?: boolean

  ui?: UIProp<'base' | 'iconLeft' | 'iconRight' | 'label'>
}

const props = defineProps<VTagProps>()

const { t, te } = useI18n()

const computedColor = computed(() => {
  let color = props.color
  if (!color) {
    color = 'gray'
  }

  let baseClass = `bg-${color} text-${color}-text`

  if (props.textColor) {
    baseClass = twMerge(baseClass, `text-${props.textColor}`)
  }

  if (props.darkColor) {
    baseClass = twMerge(
      baseClass,
      `dark:bg-${props.darkColor} dark:text-${props.darkColor}-text`
    )
  }

  if (props.darkTextColor) {
    baseClass = twMerge(baseClass, `dark:text-${props.darkTextColor}`)
  }

  return baseClass
})

const computedSize = computed(() => {
  if (props.size) {
    return props.size
  }
  return 'normal'
})

const attrs = useAttrs()
const computedClickable = computed(
  () => !!attrs.onClick && !props.overrideClickable && 'cursor-pointer'
)
</script>

<template>
  <component
    :is="attrs.onClick ? VHtmlButton : 'div'"
    :style="{ backgroundColor: stringColor, color: cssTextColor }"
    class="tag"
    :class="[
      computedColor,
      computedSize,
      computedClickable,
      color === 'white' && 'border-bg-inverted border border-solid',
      rounded && 'rounded-full',
      isDelete && 'px-0! hover:brightness-90',
      ui?.base
    ]"
  >
    <slot>
      <VIcon v-if="iconLeft" :icon="iconLeft" :color="iconColor" :class="ui?.iconLeft" />
      <span v-if="label" :class="ui?.label">{{ te(label) ? t(label) : label }}</span>
      <VIcon
        v-if="iconRight"
        :icon="iconRight"
        :color="iconColor"
        :class="ui?.iconRight"
      />
    </slot>
  </component>
</template>

<style scoped></style>
